import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Paper, Typography, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";
import StatsControls from "./StatsControls";
import StatsEleves from "./StatsEleves";

/**
 * StatsClasse (updated)
 * - Shows StatsControls only when isProf true (server-validated OR local password unlocked)
 * - Adds "Modifier mot de passe Paramètres" dialog for profs to change the local password.
 */

const LOCAL_PWD_KEY = 'jugeboxe_admin_hash';
const SESSION_FLAG = 'jugeboxe_auth';

async function sha256Hex(str) {
  const enc = new TextEncoder().encode(str);
  const hashBuffer = await crypto.subtle.digest('SHA-256', enc);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export default function StatsClasse() {
  const location = useLocation();
  const navigate = useNavigate();
  const q = new URLSearchParams(location.search);
  const classeId = q.get("classeId");
  const activity = q.get("activity");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [classeName, setClasseName] = useState("");
  const [rows, setRows] = useState([]);
  const [fetchedAny, setFetchedAny] = useState(false);
  const [reloadCounter, setReloadCounter] = useState(0);
  const [isProf, setIsProf] = useState(false);
  const [showPodium, setShowPodium] = useState(false);

  // change password dialog
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  const [curPwd, setCurPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');

  const triggerReload = useCallback(() => {
    setReloadCounter(c => c + 1);
  }, []);

  useEffect(() => {
    let mounted = true;

    const validateProfToken = async (token) => {
      try {
        const res = await api.get('/users/me', { headers: { Authorization: `Bearer ${token}` } });
        const role = res?.data?.role || res?.data?.user?.role;
        return role === 'prof' || role === 'teacher' || role === 'enseignant';
      } catch (err) {
        return false;
      }
    };

    const detectProf = async () => {
      try {
        // 1) session flag from local password mode
        const sessionFlag = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(SESSION_FLAG) : null;
        if (sessionFlag === '1') {
          if (mounted) setIsProf(true);
          return;
        }

        // 2) try validate possible stored profAuthToken
        const sessionToken = typeof sessionStorage !== "undefined" ? sessionStorage.getItem('profAuthToken') : null;
        if (sessionToken) {
          const ok = await validateProfToken(sessionToken);
          if (ok) { if (mounted) setIsProf(true); return; }
          try { sessionStorage.removeItem('profAuthToken'); } catch(e){}
        }

        // 3) try default /users/me with current axios auth
        try {
          const me = await api.get('/users/me');
          const role = me?.data?.role || me?.data?.user?.role;
          if (role === 'prof' || role === 'teacher' || role === 'enseignant') {
            if (mounted) setIsProf(true);
            return;
          }
        } catch (err) {
          // ignore
        }

        // 4) fallback: decode local token's payload if present
        try {
          const token = localStorage.getItem('token');
          if (token) {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const role = payload?.role || payload?.user?.role;
            if (role === 'prof' || role === 'teacher' || role === 'enseignant') {
              if (mounted) setIsProf(true);
              return;
            }
          }
        } catch (e) {
          // ignore
        }

        if (mounted) setIsProf(false);
      } catch (e) {
        if (mounted) setIsProf(false);
      }
    };

    detectProf();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadClasseName() {
      if (!classeId) return;
      try {
        const res = await api.get(`/classes/${encodeURIComponent(classeId)}`);
        if (!mounted) return;
        const c = res.data || {};
        const name = c.nom ?? c.name ?? c.label ?? c.title ?? String(classeId);
        setClasseName(name);
      } catch (err) {
        setClasseName(String(classeId));
      }
    }

    async function tryFetchEleves(id) {
      const attempts = [
        `/classes/${encodeURIComponent(id)}/eleves`,
        `/eleves?classeId=${encodeURIComponent(id)}`,
        `/eleves?classe=${encodeURIComponent(id)}`,
        `/students?classeId=${encodeURIComponent(id)}`,
        `/students?classe=${encodeURIComponent(id)}`
      ];
      for (const url of attempts) {
        try {
          const res = await api.get(url);
          if (Array.isArray(res.data)) return res.data;
          if (res.data && Array.isArray(res.data.data)) return res.data.data;
          if (res.data && Array.isArray(res.data.items)) return res.data.items;
        } catch (err) {
          // continue
        }
      }
      throw new Error("Aucun endpoint élèves trouvé pour cette classe");
    }

    async function load() {
      if (!classeId) {
        setError("Paramètre classeId manquant dans l'URL.");
        return;
      }
      if (!activity) {
        setError("Paramètre activity manquant dans l'URL.");
        return;
      }
      setLoading(true);
      setError("");
      setRows([]);
      setFetchedAny(false);

      try {
        await loadClasseName();

        let eleves = [];
        try {
          eleves = await tryFetchEleves(classeId);
        } catch (err) {
          console.warn("StatsClasse: récupération élèves a échoué", err);
          eleves = [];
        }

        if (!mounted) return;

        if (!Array.isArray(eleves) || eleves.length === 0) {
          setFetchedAny(false);
          setRows([]);
          setError("Aucun élève trouvé pour cette classe (vérifier l'API).");
          return;
        }

        const mapped = eleves.map(e => {
          const id = String(e._id ?? e.id ?? e.id_eleve ?? "");
          const prenom = e.prenom ?? e.firstname ?? "";
          const nom = e.nom ?? e.lastname ?? e.name ?? "";
          const sba = e.statsByActivity ?? {};
          const act = sba[activity] ?? {};
          const wins = act.wins ?? 0;
          const losses = act.losses ?? 0;
          const arbitres = act.arbitres ?? 0;
          return { id, nom, prenom, wins, losses, arbitres };
        });

        // sort by wins desc, then losses asc, then nom/prenom
        mapped.sort((a, b) => {
          if ((b.wins ?? 0) !== (a.wins ?? 0)) return (b.wins ?? 0) - (a.wins ?? 0);
          if ((a.losses ?? 0) !== (b.losses ?? 0)) return (a.losses ?? 0) - (b.losses ?? 0);
          if (a.nom !== b.nom) return (a.nom || "").localeCompare(b.nom || "");
          return (a.prenom || "").localeCompare(b.prenom || "");
        });

        setRows(mapped);
        setFetchedAny(true);
      } catch (err) {
        console.error("StatsClasse load error:", err);
        setError(err?.message || "Erreur lors du chargement des statistiques.");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classeId, activity, reloadCounter]);

  // change password dialog handlers
  const openPwdDialog = () => {
    setPwdMsg('');
    setCurPwd('');
    setNewPwd('');
    setConfirmPwd('');
    setPwdDialogOpen(true);
  };
  const closePwdDialog = () => setPwdDialogOpen(false);

  const handleChangePassword = async () => {
    setPwdMsg('');
    // if no existing password, allow set directly
    const existingHash = localStorage.getItem(LOCAL_PWD_KEY);
    if (existingHash) {
      if (!curPwd) { setPwdMsg('Mot de passe actuel requis'); return; }
      const curHash = await sha256Hex(curPwd);
      if (curHash !== existingHash) { setPwdMsg('Mot de passe actuel incorrect'); return; }
    }
    if (!newPwd || newPwd.length < 4) { setPwdMsg('Nouveau mot de passe trop court (min 4)'); return; }
    if (newPwd !== confirmPwd) { setPwdMsg('La confirmation ne correspond pas'); return; }
    const newHash = await sha256Hex(newPwd);
    localStorage.setItem(LOCAL_PWD_KEY, newHash);
    // set session unlocked
    try { sessionStorage.setItem(SESSION_FLAG, '1'); } catch (e) {}
    setPwdMsg('Mot de passe mis à jour.');
    setTimeout(() => { closePwdDialog(); }, 900);
  };

  // medal helper & navigate
  const medalForIndex = (i) => {
    if (i === 0) return "🥇";
    if (i === 1) return "🥈";
    if (i === 2) return "🥉";
    return "";
  };
  const openStudent = (id) => { if (!id) return; navigate(`/eleve/${encodeURIComponent(id)}?activity=${encodeURIComponent(activity)}`); };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa", display: "flex", justifyContent: "center", alignItems: "flex-start", py: 3 }}>
      <Paper sx={{ width: { xs: "95%", sm: 900 }, p: 3 }}>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h5">Stats — {activity ?? "Activité"}</Typography>
          <Typography variant="subtitle1" sx={{ color: "#666" }}>{classeName ? `${classeName}` : `Classe ${classeId}`}</Typography>
        </Box>

        {/* Controls for admin: reset / recompute (only visible to prof) */}
        {isProf ? <StatsControls onDone={() => triggerReload()} /> : null}

        {/* password management btn for prof */}
        {isProf ? (
          <Box sx={{ mt: 2 }}>
            <Button variant="outlined" onClick={openPwdDialog}>Modifier le mot de passe Paramètres</Button>
          </Box>
        ) : null}

        {/* toggle podium */}
        <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
          <Button variant={showPodium ? "outlined" : "contained"} onClick={() => setShowPodium(true)}>Voir podium</Button>
          <Button variant={!showPodium ? "outlined" : "contained"} onClick={() => setShowPodium(false)}>Voir tableau</Button>
        </Box>

        {loading && <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}><CircularProgress /></Box>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && !error && fetchedAny && (
          <>
            {showPodium ? (
              <StatsEleves classeId={classeId} />
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>NOM</TableCell>
                        <TableCell>Prénom</TableCell>
                        <TableCell align="right">Victoires</TableCell>
                        <TableCell align="right">Défaites</TableCell>
                        <TableCell align="right">Rôle Juge</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((r, i) => (
                        <TableRow key={r.id || i} hover>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <span aria-hidden="true">{medalForIndex(i)}</span>
                              {r.id ? (
                                <Button variant="text" onClick={() => openStudent(r.id)} sx={{ textTransform: "none", p: 0 }}>
                                  {r.nom || "—"}
                                </Button>
                              ) : (<Typography>{r.nom || "—"}</Typography>)}
                            </Box>
                          </TableCell>
                          <TableCell>{r.prenom || "—"}</TableCell>
                          <TableCell align="right">{r.wins ?? 0}</TableCell>
                          <TableCell align="right">{r.losses ?? 0}</TableCell>
                          <TableCell align="right">{r.arbitres ?? 0}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {rows.length === 0 && <Typography sx={{ mt: 2 }}>Aucun élève avec des statistiques pour cette activité.</Typography>}
              </>
            )}
          </>
        )}

        {!loading && !error && !fetchedAny && (
          <Typography sx={{ mt: 2, color: "#666" }}>Aucune donnée disponible pour cette classe / activité.</Typography>
        )}

        <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
          <Button variant="outlined" onClick={() => navigate(-1)}>Retour</Button>
          <Button variant="contained" onClick={() => { setLoading(true); window.location.reload(); }}>Rafraîchir</Button>
        </Box>
      </Paper>

      {/* Password change dialog */}
      <Dialog open={pwdDialogOpen} onClose={closePwdDialog}>
        <DialogTitle>Modifier le mot de passe Paramètres</DialogTitle>
        <DialogContent>
          {localStorage.getItem(LOCAL_PWD_KEY) && (
            <TextField label="Mot de passe actuel" type="password" fullWidth margin="normal" value={curPwd} onChange={e => setCurPwd(e.target.value)} />
          )}
          <TextField label="Nouveau mot de passe" type="password" fullWidth margin="normal" value={newPwd} onChange={e => setNewPwd(e.target.value)} />
          <TextField label="Confirmer" type="password" fullWidth margin="normal" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} />
          {pwdMsg && <Typography sx={{ mt: 1, color: pwdMsg.includes('incorrect') ? 'error.main' : 'success.main' }}>{pwdMsg}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={closePwdDialog}>Annuler</Button>
          <Button variant="contained" onClick={handleChangePassword}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}